import HistoryIcon from './components/HistoryIcon';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel } from '@/shared/utils/datas';
import { GetHistoricoUsuario, TratarDiferenca } from '@/modules/system/historico/historico.service';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getEditLink, Tabelas } from '@/shared/constants/tables';
import { ptBR } from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useState } from 'react';
import { HistoricoDto, ReadUsuarioDto } from '@/api.types';
import { FindHistoricoDto } from '@/api.types.extra';
import Links from '@/shared/constants/navegacao';

export default function HistoricoIndexPage() {
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)

  const [idUsuario, setIdUsuario] = useState(0);
  const [dataInicial, setDataInicial] = useState(primeiroDia);
  const [dataFinal, setDataFinal] = useState(ultimoDia);
  const [textoBusca, setTextoBusca] = useState('');
  const [tabela, setTabela] = useState('');
  const [loaded, setLoaded] = useState(false)
  const [buscando, setBuscando] = useState(false)

  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);
  const [registros, setRegistros] = useState<[HistoricoDto[], number]>();
  const tableState = useDataTableState('criadoEm');

  useEffect(() => {
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
        if (ordenados.length > 0)
          setIdUsuario(ordenados[0].id);
      })
      .catch(console.warn)
      .finally(() => {
        setLoaded(true)
      })
  }, []);

  function handleBuscar() {
    if (loaded) {

      setBuscando(true)
      const nomeUsuario = listaUsuarios.find(u => u.id === idUsuario)?.pessoa?.nome;
      setTextoBusca(`Log de: ${nomeUsuario} no período de ${DataAmigavel(dataInicial)} à ${DataAmigavel(dataFinal)}.`)

      const filters: FindHistoricoDto = {
        criadopor: idUsuario,
        data_inicial: dataInicial.toISOString().slice(0, 10),
        data_final: dataFinal.toISOString().slice(0, 10),
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        tabela: tabela,
        orderBy: tableState.sortConfig.direction,
        orderField: tableState.sortConfig.key
      }

      GetHistoricoUsuario(filters)
        .then((result) => {
          setRegistros(result);
        })
        .catch((res) => {
          toast.error(res)
        })
        .finally(() => {
          setBuscando(false)
        })
    }
  }

  useEffect(() => {
    handleBuscar();
  }, [loaded])

  let colunas: Column<HistoricoDto>[] = [
    { key: "criadoEm", header: "Data", render: (row) => format(new Date(row.criadoEm), "dd/MM/yyyy HH:mm", { locale: ptBR }) },
    { key: "acao", header: "Ação" },
    { key: "criadoPor.username", header: "Usuário", invisible: true },
    { key: "tabela", header: "Tabela" },
    { key: "registro", header: "Registro", invisible: true },
    {
      key: "resumo", header: "Resumo",
      render: (row) => {
        const diffMap = TratarDiferenca(row.diff)
        const diffEntries = Object.entries(diffMap);
        return (
          <>
            {diffEntries.map(([key, value]) => (
              <HistoryIcon key={key} fieldNumber={Number(key)} value={value} />
            ))}
          </>
        )
      }
    },
    {
      key: "acoes", header: "Ações", render: (row) => {
        const editLink = getEditLink(row.tabela, row.registro);
        return (
          <>
            {editLink && <Link className="link-secondary" to={editLink} target='_blank'>
              Editar
            </Link>}
          </>
        )
      }
    },
  ];

  return (
    <div className="row">
      <div className="col-md-8">
        <h3>Logs de Uso</h3>
        <span className="blockquote-footer pb-4 text-end">
          Log de Alterações do Sistema
        </span>
      </div>
      <hr />

      <div className="col-12 mb-3 d-flex gap-2">
        <Link to={Links.Auditorias_Index} className="btn btn-outline-primary btn-sm">
          Voltar para Auditorias
        </Link>
      </div>

      {registros &&
        <>
          <DataTable
            columns={colunas}
            data={registros?.[0]}
            initialSortKey="estagios.0.data_inicio_estagio"
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros?.[1]}
            renderFilters={() => (
              <div className='row'>
                <div className="col-md-3">
                  <label htmlFor="usuario" className="col-form-label">Usuário:</label>
                  <select
                    id="usuario"
                    title="Usuário"
                    className="form-select"
                    value={idUsuario}
                    onChange={(e) => { setIdUsuario(Number(e.target.value)) }}
                  >
                    <option value="">-</option>
                    {listaUsuarios.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label htmlFor="tabela" className="col-form-label">Tabela:</label>
                  <select
                    id="tabela"
                    title="Tabela"
                    className="form-select"
                    value={tabela}
                    onChange={(e) => { setTabela(e.target.value) }}
                  >
                    <option value="">-</option>
                    {Object.entries(Tabelas).map((item) => (
                      <option key={item[0]} value={item[0]}>
                        {item[1]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label htmlFor="data_inicial" className="col-form-label">Data Inicial:</label>
                  <input
                    id="data_inicial"
                    type="date"
                    className="form-control"
                    value={
                      dataInicial instanceof Date && !isNaN(dataInicial.getTime())
                        ? dataInicial.toISOString().slice(0, 10)
                        : ""
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataInicial(new Date(e.target.value))}
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="data_final" className="col-form-label">Data Final:</label>
                  <input
                    id="data_final"
                    type="date"
                    className="form-control"
                    value={
                      dataFinal instanceof Date && !isNaN(dataFinal.getTime())
                        ? dataFinal.toISOString().slice(0, 10)
                        : ""
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataFinal(new Date(e.target.value))}
                  />
                </div>

                <div className="col-md-2 mt-3 pt-4">
                  <button className="btn btn-sm btn-outline-success" onClick={handleBuscar} disabled={buscando}>
                    {buscando ? 'Buscando...' : 'Pesquisar'}
                  </button>
                </div>

                <div className='mt-3 mb-2 col-12'>
                  <span className='text-muted small'>{textoBusca}</span>
                </div>
              </div>
            )}
          />
        </>
      }
    </div>
  );
}
